/**
 * FileAttachment - Display files in chat (WhatsApp-style)
 *
 * Shows a preview/thumbnail of the file with click to open in modal.
 * Supports: images, audio, video, documents (PDF, Word, etc.)
 */

import React, { useState, useMemo } from 'react';
import {
  FileText,
  Image as ImageIcon,
  Music,
  Video,
  File,
  Download,
  Play,
  FileSpreadsheet,
  Presentation,
  Archive,
} from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

export interface FileAttachmentData {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  url: string;
}

interface FileAttachmentProps {
  file: FileAttachmentData;
  /** Click to open preview modal */
  onClick?: (file: FileAttachmentData) => void;
  /** Compact mode for chat bubbles */
  compact?: boolean;
  /** User message (right aligned) vs assistant (left aligned) */
  isUser?: boolean;
}

// =============================================================================
// HELPERS
// =============================================================================

type FileCategory = 'image' | 'audio' | 'video' | 'pdf' | 'document' | 'spreadsheet' | 'presentation' | 'archive' | 'other';

function getFileCategory(mimeType: string): FileCategory {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType === 'application/pdf') return 'pdf';
  if (
    mimeType.includes('word') ||
    mimeType.includes('document') ||
    mimeType === 'text/plain' ||
    mimeType === 'text/rtf' ||
    mimeType === 'application/rtf'
  ) return 'document';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || mimeType === 'text/csv') return 'spreadsheet';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'presentation';
  if (
    mimeType.includes('zip') ||
    mimeType.includes('rar') ||
    mimeType.includes('7z') ||
    mimeType.includes('tar') ||
    mimeType.includes('gzip')
  ) return 'archive';
  return 'other';
}

function getFileIcon(category: FileCategory) {
  switch (category) {
    case 'image': return ImageIcon;
    case 'audio': return Music;
    case 'video': return Video;
    case 'pdf': return FileText;
    case 'document': return FileText;
    case 'spreadsheet': return FileSpreadsheet;
    case 'presentation': return Presentation;
    case 'archive': return Archive;
    default: return File;
  }
}

function getCategoryColor(category: FileCategory): string {
  switch (category) {
    case 'image': return '#10b981'; // green
    case 'audio': return '#f59e0b'; // amber
    case 'video': return '#ef4444'; // red
    case 'pdf': return '#dc2626'; // red-600
    case 'document': return '#3b82f6'; // blue
    case 'spreadsheet': return '#22c55e'; // green-500
    case 'presentation': return '#f97316'; // orange
    case 'archive': return '#6b7280'; // gray
    default: return '#8b5cf6'; // purple
  }
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toUpperCase() || '';
}

// =============================================================================
// COMPONENT
// =============================================================================

export function FileAttachment({
  file,
  onClick,
  compact = false,
  isUser = false,
}: FileAttachmentProps) {
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const category = useMemo(() => getFileCategory(file.mimeType), [file.mimeType]);
  const Icon = useMemo(() => getFileIcon(category), [category]);
  const color = useMemo(() => getCategoryColor(category), [category]);
  const extension = useMemo(() => getFileExtension(file.filename), [file.filename]);

  const handleClick = () => {
    onClick?.(file);
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(file.url, '_blank');
  };

  // Image preview
  if (category === 'image' && !imageError) {
    return (
      <div
        style={{
          ...styles.imageContainer,
          maxWidth: compact ? 200 : 280,
        }}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <img
          src={file.url}
          alt={file.filename}
          style={styles.image}
          onError={() => setImageError(true)}
        />
        {isHovered && (
          <div style={styles.imageOverlay}>
            <span style={styles.imageFilename}>{file.filename}</span>
          </div>
        )}
      </div>
    );
  }

  // Audio preview with play button
  if (category === 'audio') {
    return (
      <div
        style={{
          ...styles.audioContainer,
          background: isUser ? 'rgba(255,255,255,0.15)' : '#f3f4f6',
        }}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={{ ...styles.playButton, background: color }}>
          <Play size={20} fill="#fff" color="#fff" />
        </div>
        <div style={styles.audioInfo}>
          <span style={{
            ...styles.filename,
            color: isUser ? '#fff' : '#1f2937',
          }}>
            {file.filename.length > 25 ? file.filename.slice(0, 22) + '...' : file.filename}
          </span>
          <span style={{
            ...styles.meta,
            color: isUser ? 'rgba(255,255,255,0.7)' : '#6b7280',
          }}>
            Audio - {formatFileSize(file.size)}
          </span>
        </div>
        <button
          onClick={handleDownload}
          style={styles.downloadBtn}
          title="Download"
        >
          <Download size={16} color={isUser ? 'rgba(255,255,255,0.7)' : '#6b7280'} />
        </button>
      </div>
    );
  }

  // Video preview
  if (category === 'video') {
    return (
      <div
        style={styles.videoContainer}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <video
          src={file.url}
          style={styles.video}
          preload="metadata"
        />
        <div style={styles.videoOverlay}>
          <div style={{ ...styles.playButton, background: color }}>
            <Play size={24} fill="#fff" color="#fff" />
          </div>
        </div>
        <div style={styles.videoInfo}>
          <span style={styles.videoFilename}>{file.filename}</span>
          <span style={styles.videoMeta}>{formatFileSize(file.size)}</span>
        </div>
      </div>
    );
  }

  // Document/file preview (PDF, Word, Excel, etc.)
  return (
    <div
      style={{
        ...styles.fileContainer,
        background: isUser ? 'rgba(255,255,255,0.15)' : '#fff',
        border: isUser ? 'none' : '1px solid #e5e7eb',
      }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Icon with extension badge */}
      <div style={{ ...styles.iconContainer, background: `${color}15` }}>
        <Icon size={24} color={color} />
        <span style={{ ...styles.extensionBadge, background: color }}>
          {extension}
        </span>
      </div>

      {/* File info */}
      <div style={styles.fileInfo}>
        <span style={{
          ...styles.filename,
          color: isUser ? '#fff' : '#1f2937',
        }}>
          {file.filename.length > 30 ? file.filename.slice(0, 27) + '...' : file.filename}
        </span>
        <span style={{
          ...styles.meta,
          color: isUser ? 'rgba(255,255,255,0.7)' : '#6b7280',
        }}>
          {formatFileSize(file.size)}
        </span>
      </div>

      {/* Download button */}
      <button
        onClick={handleDownload}
        style={{
          ...styles.downloadBtn,
          opacity: isHovered ? 1 : 0.7,
        }}
        title="Download"
      >
        <Download size={18} color={isUser ? 'rgba(255,255,255,0.7)' : '#6b7280'} />
      </button>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  // Image
  imageContainer: {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  image: {
    width: '100%',
    height: 'auto',
    display: 'block',
    borderRadius: '12px',
  },
  imageOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
    padding: '20px 10px 8px',
  },
  imageFilename: {
    color: '#fff',
    fontSize: '12px',
    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
  },

  // Audio
  audioContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '12px',
    cursor: 'pointer',
    minWidth: 200,
    maxWidth: 280,
  },
  playButton: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  audioInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },

  // Video
  videoContainer: {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    cursor: 'pointer',
    maxWidth: 280,
  },
  video: {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
  videoOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.3)',
  },
  videoInfo: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'rgba(0,0,0,0.7)',
    padding: '8px 10px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  videoFilename: {
    color: '#fff',
    fontSize: '12px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  videoMeta: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: '11px',
    flexShrink: 0,
    marginLeft: '8px',
  },

  // File/Document
  fileContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '12px',
    cursor: 'pointer',
    minWidth: 200,
    maxWidth: 300,
    transition: 'box-shadow 0.15s ease',
  },
  iconContainer: {
    position: 'relative',
    width: 44,
    height: 44,
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  extensionBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    padding: '1px 4px',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '8px',
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  fileInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  filename: {
    fontSize: '13px',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    fontSize: '11px',
  },
  downloadBtn: {
    background: 'transparent',
    border: 'none',
    padding: '6px',
    cursor: 'pointer',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.15s ease',
  },
};

export default FileAttachment;
